import _memoize from 'lodash/memoize';
import { CLOUD_NORMAL, HIGHLIGHT, makeThemeColor, PRIMARY, TERTIARY } from '@tornado-rdk/theme';
import { FONT, getFont } from '@tornado-rdk/typography';
import { FULL_HD, HD } from '@bedrock-rdk/constants';
import { makeDefaultStylingContext } from '../../../../utils/style';
import { HEIGHT as PROGRESS_HEIGHT } from '../../../../progress/progress.style';

export const defaultContext = makeDefaultStylingContext();

export const colors = {
  backgroundUnfocused: makeThemeColor(HIGHLIGHT),
  backgroundFocused: makeThemeColor(CLOUD_NORMAL),
  border: makeThemeColor(PRIMARY),
  text: makeThemeColor(PRIMARY),
  filledColor: makeThemeColor(TERTIARY),
  gradientFill: makeThemeColor(TERTIARY),
  transparent: makeThemeColor(TERTIARY, 0),
};

export const SCREEN_SIZE = {
  WIDTH: {
    [HD]: 1280,
    [FULL_HD]: 1920,
  },
  HEIGHT: {
    [HD]: 720,
    [FULL_HD]: 1080,
  },
};
export const ITEM_WIDTH = {
  [HD]: 616,
  [FULL_HD]: 680,
};

export const IMAGE_DIMENSIONS = {
  [HD]: {
    width: 616,
    height: 346,
  },
  [FULL_HD]: {
    width: 680,
    height: 382,
  },
};

export const ITEM_HEIGHT = {
  [HD]: 720,
  [FULL_HD]: 1080,
};

export const GAP = {
  [HD]: 32,
  [FULL_HD]: 32,
};

export const SPACER_TOP = {
  [HD]: 18,
  [FULL_HD]: 32,
};

export const CTA_GAP = {
  [HD]: 9,
  [FULL_HD]: 16,
};

export const PICTO_MARGIN_RIGHT = {
  [HD]: 9,
  [FULL_HD]: 16,
};

export const CTA_BUTTON_MARGIN_RIGHT = {
  [HD]: 24,
  [FULL_HD]: 32,
};

export const LOGO_DIMENSIONS = {
  [HD]: {
    width: 152,
    height: 86,
  },
  [FULL_HD]: {
    width: 248,
    height: 140,
  },
};

export const LOGO_PADDING = {
  [HD]: 260,
  [FULL_HD]: 242,
};

export const TWELVE_PIXELS_PADDING = {
  [HD]: 7,
  [FULL_HD]: 12,
};
export const SPECIAL_PADDING = 168;
export const BASIC_PICTO_DIMENSION = 24;

export const DESCRIPTION_MAX_LINES = {
  [HD]: 2,
  [FULL_HD]: 5,
};

export const TITLE_MAX_LINES = {
  [HD]: 1,
  [FULL_HD]: 2,
};

export const SPACER_START = {
  [HD]: 64,
  [FULL_HD]: 96,
};

export const SPACER_END = {
  [HD]: 64,
  [FULL_HD]: 96,
};

export const getInitialStyle = ({ screenSize, colorMode } = defaultContext) => ({
  x: SCREEN_SIZE.WIDTH[screenSize] - ITEM_WIDTH[screenSize],
  Background: {
    rect: true,
    color: colors.filledColor[colorMode],
    w: ITEM_WIDTH[screenSize],
    h: ITEM_HEIGHT[screenSize],
  },
  Image: {
    w: IMAGE_DIMENSIONS[screenSize].width,
    h: IMAGE_DIMENSIONS[screenSize].height,
  },
  Gradient: {
    rect: true,
    colorUl: colors.transparent[colorMode],
    colorUr: colors.transparent[colorMode],
    colorBr: colors.gradientFill[colorMode],
    colorBl: colors.gradientFill[colorMode],
    w: IMAGE_DIMENSIONS[screenSize].width,
    h: IMAGE_DIMENSIONS[screenSize].height,
  },
  Container: {
    x: SPACER_START[screenSize],
    flex: { direction: 'column' },
    LogoWrapper: {
      w: LOGO_DIMENSIONS[screenSize].width,
      h: LOGO_DIMENSIONS[screenSize].height,
      flexItem: { marginTop: LOGO_PADDING[screenSize] },
      Logo: {},
    },
    Title: {
      color: colors.text[colorMode],
      flexItem: { marginTop: SPACER_TOP[screenSize] },
      text: {
        ...getFont(FONT.PARIS_3, screenSize),
        verticalAlign: 'middle',
        wordWrapWidth: ITEM_WIDTH[screenSize] - SPACER_START[screenSize] - SPACER_END[screenSize],
        maxLines: TITLE_MAX_LINES[screenSize],
        textOverflow: '...',
      },
    },
    ProgramCardError: {
      flexItem: { marginTop: SPACER_TOP[screenSize] },
      w: ITEM_WIDTH[screenSize] - SPACER_START[screenSize] - SPACER_END[screenSize],
    },
    CTAContainer: {
      w: ITEM_WIDTH[screenSize] - SPACER_START[screenSize],
      flexItem: { marginTop: SPACER_TOP[screenSize] },
      flex: {
        direction: 'row',
      },
      PlayButton: {
        flexItem: {
          marginRight: CTA_BUTTON_MARGIN_RIGHT[screenSize],
        },
      },
      MyListButton: {
        flexItem: {
          marginRight: CTA_BUTTON_MARGIN_RIGHT[screenSize],
        },
      },
    },
    DetailsContainer: {
      flexItem: { marginTop: SPACER_TOP[screenSize] },
      flex: { direction: 'column' },
      w: ITEM_WIDTH[screenSize] - SPACER_START[screenSize] - SPACER_END[screenSize],
      Details: {
        color: colors.text[colorMode],
        w: ITEM_WIDTH[screenSize] - SPACER_START[screenSize] - SPACER_END[screenSize],
        text: {
          ...getFont(FONT.OTTAWA_3, screenSize),
          verticalAlign: 'middle',
          wordWrapWidth: ITEM_WIDTH[screenSize] - SPACER_START[screenSize] - SPACER_END[screenSize],
          wordWrap: false,
          textOverflow: '...',
        },
      },
      Time: {
        color: colors.text[colorMode],
        flexItem: { marginTop: CTA_GAP[screenSize] },
        text: {
          ...getFont(FONT.OTTAWA_3, screenSize),
          verticalAlign: 'middle',
          wordWrap: false,
          textOverflow: '...',
        },
      },
      ProgressBar: {
        color: colors.backgroundUnfocused[colorMode],
        w: ITEM_WIDTH[screenSize] - SPACER_START[screenSize] - SPACER_END[screenSize],
        h: PROGRESS_HEIGHT,
        flexItem: { marginTop: CTA_GAP[screenSize] },
      },
      Description: {
        color: colors.text[colorMode],
        w: ITEM_WIDTH[screenSize] - SPACER_START[screenSize] - SPACER_END[screenSize],
        text: {
          ...getFont(FONT.OTTAWA_1, screenSize),
          verticalAlign: 'middle',
          maxLines: DESCRIPTION_MAX_LINES[screenSize],
          textOverflow: '...',
        },
      },
      ExtraDetails: {
        color: colors.text[colorMode],
        flexItem: { marginTop: TWELVE_PIXELS_PADDING[screenSize] },
        w: ITEM_WIDTH[screenSize] - SPACER_START[screenSize] - SPACER_END[screenSize],
        text: {
          ...getFont(FONT.BERLIN_2, screenSize),
          verticalAlign: 'middle',
          maxLines: 3,
          textOverflow: '...',
        },
      },
      IconRow: {
        flex: {
          direction: 'row',
        },
        ServiceIcon: {
          flexItem: { marginTop: TWELVE_PIXELS_PADDING[screenSize], marginRight: PICTO_MARGIN_RIGHT[screenSize] },
        },
        Picto1: {
          flexItem: { marginTop: TWELVE_PIXELS_PADDING[screenSize], marginRight: PICTO_MARGIN_RIGHT[screenSize] },
        },
      },
      Button: {
        flexItem: { marginTop: TWELVE_PIXELS_PADDING[screenSize] },
      },
    },
  },
});

export const getBasicIconViewBox = _memoize(() => ({
  h: BASIC_PICTO_DIMENSION,
}));
